type Screen = "home" | "processing" | "display" | "camera" | "cameraPicture" | "openFilePicture" | "pdfView" | "error"

// Temporary screens that should not be persisted in navigation history
const TEMPORARY_SCREENS: Screen[] = ["processing", "display"];

const NAV_STACK_STORAGE_KEY = 'dyslexa_nav_stack';

class NavigationStack {
    private state: NavigationStackState;
    private listeners: ((stack: Screen[]) => void)[] = [];
    private isJumping = false;

    constructor(initialScreen: Screen = 'home') {
        // Initialize with default screen - restore from storage will happen on client side
        this.state = new NavigationStackState([initialScreen]);
    }

    public restoreFromStorage(): void {
        // Only restore from storage on client side after mount
        if (typeof window !== 'undefined') {
            const savedStack = NavigationStackState.loadFromStorage();
            if (savedStack && savedStack.length > 0) {
                // Validate that all screens in the stack are valid
                const validScreens: Screen[] = ["home", "processing", "display", "camera", "cameraPicture", "openFilePicture", "pdfView", "error"];
                // Exclude temporary screens that should not be persisted
                const validatedStack = savedStack
                    .filter(screen => validScreens.includes(screen as Screen)) // Valid screens only
                    .filter(screen => !TEMPORARY_SCREENS.includes(screen as Screen)) as Screen[]; // Exclude temporary screens
                
                // If validation removed all screens, default to home
                if (validatedStack.length === 0) {
                    this.state = new NavigationStackState(['home']);
                } else {
                    this.state = new NavigationStackState(validatedStack);
                }
                this.notify();
            }
        }
    }

    getCurrentScreen(): Screen {
        const stack = this.state.getStack();
        return stack[stack.length - 1];
    }

    getStack(): Screen[] {
        return this.state.getStack();
    }

    onChange(listener: (stack: Screen[]) => void) {
        this.listeners.push(listener);
    }

    private notify() {
        for (const listener of this.listeners) {
            listener(this.getStack());
        }
    }

    jumpTo(screen: Screen): void {
        const stack = this.state.getStack();
        // Prevent duplicate consecutive entries
        if (stack[stack.length - 1] === screen) {
            return;
        }
        stack.push(screen);
        this.state.setStack(stack);
        this.isJumping = true;
        this.state.saveToStorage();
        this.notify();
    }

    navigateTo(screen: Screen): void {
        const stack = this.state.getStack();
        if (this.isJumping) {
            stack.pop(); // Remove the temporary jump screen
            this.isJumping = false;
        }
        // Prevent duplicate consecutive entries
        if (stack[stack.length - 1] === screen) {
            return;
        }
        stack.push(screen);
        this.state.setStack(stack);
        this.state.saveToStorage();
        this.notify();
    }

    goBack(): void {
        const stack = this.state.getStack();
        const currentScreen = stack[stack.length - 1];
        
        // Clear jumping state when going back
        this.isJumping = false;
        
        // If we're on processing or display screen, remove them and any other temporary screens
        if (TEMPORARY_SCREENS.includes(currentScreen)) {
            // Remove all temporary screens from the end of the stack
            while (stack.length > 0 && TEMPORARY_SCREENS.includes(stack[stack.length - 1])) {
                stack.pop();
            }
        } else {
            // Normal back behavior for other screens
            if (stack.length > 1) {
                stack.pop();
            }
        }
        
        // Ensure we always have at least one screen
        if (stack.length === 0) {
            stack.push('home');
        }
        
        this.state.setStack(stack);
        this.state.saveToStorage();
        this.notify();
    }

    reset(): void {
        const stack = this.state.getStack();
        // Clear jumping state on reset
        this.isJumping = false;
        this.state.setStack([stack[0] || 'home']);
        this.state.saveToStorage();
        this.notify();
    }

    public clearStorage(): void {
        this.isJumping = false;
        this.state.clearStorage();
    }

}

class NavigationStackState {
    private stack: Screen[];

    constructor(stack: Screen[] = []) {
        this.stack = stack;
    }

    public getStack(): Screen[] {
        return [...this.stack];
    }

    public setStack(stack: Screen[]): void {
        this.stack = stack;
    }

    public saveToStorage(): void {
        if (typeof window !== 'undefined') {
            try {
                // Exclude temporary screens from being saved
                const stackToSave = this.stack.filter(screen => !TEMPORARY_SCREENS.includes(screen));
                sessionStorage.setItem(NAV_STACK_STORAGE_KEY, JSON.stringify(stackToSave));
            } catch (e) {
                console.warn('Failed to save navigation stack to storage', e);
            }
        }
    }

    public static loadFromStorage(): Screen[] | null {
        if (typeof window !== 'undefined') {
            try {
                const saved = sessionStorage.getItem(NAV_STACK_STORAGE_KEY);
                return saved ? JSON.parse(saved) as Screen[] : null;
            } catch (e) {
                console.warn('Failed to load navigation stack from storage', e);
                return null;
            }
        }
        return null;
    }

    public clearStorage(): void {
        if (typeof window !== 'undefined') {
            try {
                sessionStorage.removeItem(NAV_STACK_STORAGE_KEY);
            } catch (e) {
                console.warn('Failed to clear navigation stack from storage', e);
            }
        }
    }
}

export { NavigationStack };